import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { getLoadingStatus } from '@process-advisor/process-miner/processMiner/processMiner';
import { Event } from '@process-advisor/process-miner/api/events';
import Backdrop from '@mui/material/Backdrop';

const PROGRESS_PREFIX = '__PA_PROGRESS__';

type ProgressState = {
    mode?: 'ai' | 'extraction' | 'generic';
    phase: string;
    percent: number;
    detail?: string;
    primaryLabel?: string;
    primaryValue?: string;
    secondaryLabel?: string;
    secondaryValue?: string;
    note?: string;
    runId?: string;
    seq?: number;
};

const Card = styled.div`
    width: 520px;
    min-height: 230px;
    box-sizing: border-box;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 22px 58px rgba(15, 23, 42, 0.34);
    padding: 28px 30px 26px 30px;
    font-family: Arial, Helvetica, sans-serif;
    color: #172b4d;
    overflow: hidden;
`;

const TopRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 10px;
`;

const Eyebrow = styled.div`
    font-size: 12px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #5e6c84;
    margin-bottom: 10px;
`;

const Phase = styled.div`
    font-size: 23px;
    line-height: 1.22;
    font-weight: 700;
    color: #172b4d;
    max-width: 350px;
`;

const Percent = styled.div`
    font-size: 34px;
    line-height: 1;
    font-weight: 700;
    color: #0c66e4;
    white-space: nowrap;
`;

const Detail = styled.div`
    height: 22px;
    font-size: 14px;
    line-height: 22px;
    color: #44546f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 22px;
`;

const ProgressTrack = styled.div`
    height: 10px;
    border-radius: 999px;
    background: #dfe7f3;
    overflow: hidden;
    margin-bottom: 22px;
`;

const ProgressFill = styled.div<{ $width: number }>`
    height: 100%;
    width: ${({ $width }) => `${Math.max(0, Math.min(100, $width))}%`};
    border-radius: 999px;
    background: #0c66e4;
    transition: width 220ms ease;
`;

const Metrics = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 16px;
`;

const Metric = styled.div`
    border: 1px solid #dfe7f3;
    background: #f7f9fc;
    border-radius: 10px;
    padding: 13px 15px;
    box-sizing: border-box;
    min-height: 68px;
`;

const MetricLabel = styled.div`
    font-size: 11px;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #5e6c84;
    margin-bottom: 8px;
`;

const MetricValue = styled.div`
    font-size: 20px;
    line-height: 1.15;
    font-weight: 700;
    color: #172b4d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;


const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const pulse = keyframes`
    0%, 80%, 100% { opacity: 0.35; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
`;

const GenericActivity = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 26px 0 20px 0;
    padding: 16px 18px;
    border: 1px solid #dfe7f3;
    border-radius: 12px;
    background: #f8fbff;
`;

const AiActivity = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 26px 0 20px 0;
    padding: 16px 18px;
    border: 1px solid #dfe7f3;
    border-radius: 12px;
    background: #f8fbff;
`;

const AiSpinner = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 4px solid #dfe7f3;
    border-top-color: #0c66e4;
    animation: ${spin} 0.85s linear infinite;
    flex: 0 0 auto;
`;

const AiText = styled.div`
    font-size: 14px;
    line-height: 1.35;
    color: #44546f;
`;

const AiDots = styled.span`
    display: inline-flex;
    gap: 3px;
    margin-left: 4px;
    vertical-align: middle;

    span {
        width: 5px;
        height: 5px;
        border-radius: 999px;
        background: #0c66e4;
        animation: ${pulse} 1.15s ease-in-out infinite;
    }

    span:nth-child(2) { animation-delay: 0.16s; }
    span:nth-child(3) { animation-delay: 0.32s; }
`;

const Note = styled.div`
    min-height: 38px;
    font-size: 13px;
    line-height: 19px;
    color: #5e6c84;
    overflow: hidden;
`;

function clampPercent(value: unknown): number {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
}



function getAiActivityMessage(elapsedMs: number): string {
    if (elapsedMs < 2500) return 'Sending prompt';
    if (elapsedMs < 90000) return 'Generating insights';
    return 'Still generating insights';
}

function isAiProgressStatus(status: unknown): boolean {
    const text = String(status || '').toLowerCase();
    return /\b(ai|generat\w*|analys\w*|assessment|advisor report|automation|opportunit\w*|healthcheck|framework|execution strategy|trend)\b/i.test(text)
        && !/\b(processing event log|records scanned|with history|sla checked|audit history|extracting|retrieving workflows|retrieving status codes|retrieving work types)\b/i.test(text);
}

function parseLegacyStatus(raw: string): ProgressState {
    const text = String(raw || 'Preparing model extraction').trim();
    const parts = text.split('—').map(p => p.trim()).filter(Boolean);
    const phase = parts[0] || text || 'Processing model';
    const detail = parts.length > 1 ? parts.slice(1).join(' — ') : '';
    const pctMatch = text.match(/(\d{1,3})\s*%/);
    const percent = pctMatch ? clampPercent(pctMatch[1]) : 0;

    if (isAiProgressStatus(text)) {
        return {
            mode: 'ai',
            phase,
            percent: percent || 8,
            detail,
            note: 'Generating AI insights can take up to 120 seconds.',
        };
    }
    const scanned = text.match(/([\d,]+)\s*\/\s*([\d,]+)\s*scanned/i);
    const history = text.match(/([\d,]+)\s*with history/i);
    const checked = text.match(/([\d,]+)\s*\/\s*([\d,]+)\s*tasks checked/i);

    if (scanned) {
        return {
            mode: 'extraction',
            phase,
            percent,
            detail,
            primaryLabel: 'Records scanned',
            primaryValue: `${scanned[1]} / ${scanned[2]}`,
            secondaryLabel: 'With history',
            secondaryValue: history ? history[1] : '0',
            note: 'Audit history is scanned first. SLA metrics run only after the audited work items are known.',
        };
    }

    if (checked) {
        return {
            mode: 'extraction',
            phase,
            percent,
            detail,
            primaryLabel: 'SLA checked',
            primaryValue: `${checked[1]} / ${checked[2]}`,
            secondaryLabel: 'Audited items',
            secondaryValue: checked[2],
            note: 'SLA metrics are being enriched for audited work items only.',
        };
    }

    return {
        mode: 'generic',
        phase,
        percent: 0,
        detail,
        primaryLabel: '',
        primaryValue: '',
        secondaryLabel: '',
        secondaryValue: '',
        note: detail || phase || 'Working…',
    };
}

function parseProgressStatus(rawStatus: string): ProgressState {
    const raw = String(rawStatus || '').trim();
    if (raw.startsWith(PROGRESS_PREFIX)) {
        try {
            const parsed = JSON.parse(raw.slice(PROGRESS_PREFIX.length));
            return {
                mode: parsed.mode || (parsed.ai === true ? 'ai' : 'extraction'),
                phase: parsed.phase || 'Processing model',
                percent: clampPercent(parsed.percent),
                detail: parsed.detail || '',
                primaryLabel: parsed.primaryLabel || 'Status',
                primaryValue: parsed.primaryValue || 'Working',
                secondaryLabel: parsed.secondaryLabel || 'Step',
                secondaryValue: parsed.secondaryValue || 'In progress',
                note: parsed.note || (parsed.mode === 'ai' || parsed.ai === true ? 'Generating AI insights can take up to 120 seconds.' : 'Process Advisor is extracting and preparing the selected process model.'),
                runId: parsed.runId || '',
                seq: Number(parsed.seq || 0),
            };
        } catch (err) {
            return parseLegacyStatus(raw);
        }
    }
    return parseLegacyStatus(raw);
}


function parseMetricLeadingNumber(value: unknown): number | null {
    const match = String(value || '').match(/^\s*([\d,]+)/);
    if (!match) return null;
    const n = Number(match[1].replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
}

function isSelectedRecordLoadPhase(state: ProgressState): boolean {
    return String(state?.phase || '').toLowerCase().includes('loading selected records');
}

function isAuditScanPhase(state: ProgressState): boolean {
    return String(state?.phase || '').toLowerCase().includes('scanning audit');
}

function getComparableScannedCount(state: ProgressState): number | null {
    // Do not treat the "Full model access" selected-record total as a scanned count.
    // It is the total scope size (e.g. 3,818), not progress. Comparing the next
    // "Loading selected records" value of 0/3,818 against that total caused the
    // spinner to reject the real next update and appear stuck at 5%.
    if (isSelectedRecordLoadPhase(state) || isAuditScanPhase(state)) {
        return parseMetricLeadingNumber(state.primaryValue);
    }
    return null;
}

function getComparableWithHistoryCount(state: ProgressState): number | null {
    // With-history is only meaningful once audit scanning has started.
    if (isAuditScanPhase(state)) {
        return parseMetricLeadingNumber(state.secondaryValue);
    }
    return null;
}

function progressRank(state: ProgressState): number {
    const text = String(state?.phase || '').toLowerCase();

    // Terminal/failure states must always be allowed through the monotonic guard.
    if (text.includes('failed') || text.includes('error')) return 100;

    if (text.includes('preparing')) return 10;

    // Access / selected-record loading are the initial extraction phase.
    // Previously "Loading selected records" ranked as 0, so the spinner dropped it
    // after "Full model access" ranked as 20 and appeared to hang at 5%.
    if (
        text.includes('free view')
        || text.includes('full model')
        || text.includes('loading selected records')
        || text.includes('selected records')
    ) return 20;

    if (text.includes('scanning audit')) return 30;
    if (text.includes('audit history complete')) return 40;
    if (text.includes('sla')) return 50;
    if (text.includes('resolving')) return 60;
    if (text.includes('building')) return 70;
    if (text.includes('completing model')) return 85;
    if (text.includes('saving')) return 80;
    if (text.includes('complete')) return 90;
    return 0;
}

export const Spinner: React.FC = () => {
    const [aiStartedAt, setAiStartedAt] = React.useState<number | null>(null);
    const [aiElapsedMs, setAiElapsedMs] = React.useState(0);
    const lastProgressRef = React.useRef<{ runId: string; seq: number; rank: number; percent: number; scanned: number; withHistory: number }>({
        runId: '',
        seq: 0,
        rank: 0,
        percent: 0,
        scanned: 0,
        withHistory: 0,
    });
    const [status, setStatus] = React.useState<string>(
        `${PROGRESS_PREFIX}${JSON.stringify({
            mode: 'extraction',
            phase: 'Preparing model extraction',
            percent: 1,
            detail: 'Starting model extraction',
            primaryLabel: 'Status',
            primaryValue: 'Preparing',
            secondaryLabel: 'Step',
            secondaryValue: 'Starting',
            note: 'Process Advisor is preparing the selected process model.',
        })}`
    );

    React.useEffect(() => {
        const listener = () => {
            const nextStatus = getLoadingStatus() || status;
            const parsedNext = parseProgressStatus(nextStatus);
            const last = lastProgressRef.current;
            const nextRank = progressRank(parsedNext);
            const nextPercent = Number(parsedNext.percent || 0);
            const nextScanned = getComparableScannedCount(parsedNext);
            const nextWithHistory = getComparableWithHistoryCount(parsedNext);

            const isTerminalFailure = nextRank >= 100;
            if (!isTerminalFailure && parsedNext.runId && parsedNext.seq && last.runId === parsedNext.runId && parsedNext.seq < last.seq) return;
            if (!isTerminalFailure && nextRank < last.rank) return;
            if (!isTerminalFailure && nextRank === last.rank && nextPercent + 1 < last.percent) return;
            if (!isTerminalFailure && nextScanned !== null && nextScanned < last.scanned) return;
            if (!isTerminalFailure && nextWithHistory !== null && nextWithHistory < last.withHistory) return;

            lastProgressRef.current = {
                runId: parsedNext.runId || last.runId,
                seq: parsedNext.seq || last.seq,
                rank: Math.max(last.rank, nextRank),
                percent: Math.max(last.percent, nextPercent),
                scanned: Math.max(last.scanned, nextScanned ?? last.scanned),
                withHistory: Math.max(last.withHistory, nextWithHistory ?? last.withHistory),
            };
            setStatus(nextStatus);
            if (isAiProgressStatus(nextStatus)) {
                setAiStartedAt(prev => prev ?? Date.now());
            } else {
                setAiStartedAt(null);
                setAiElapsedMs(0);
            }
        };

        listener();
        apiProvider.on(Event.updateLoadingStatus, listener);

        return () => {
            apiProvider.off(Event.updateLoadingStatus, listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (aiStartedAt === null) return;
        const timer = window.setInterval(() => {
            setAiElapsedMs(Date.now() - aiStartedAt);
        }, 500);
        return () => window.clearInterval(timer);
    }, [aiStartedAt]);

    const parsed = parseProgressStatus(status);
    const percent = clampPercent(parsed.percent);
    // Structured extraction payloads may contain words such as "assessment credit",
    // but they are still model-processing progress, not AI generation. Trust the
    // parsed mode; legacy text statuses are already converted to mode="ai" by
    // parseLegacyStatus when appropriate.
    const isAi = parsed.mode === 'ai';
    const isExtraction = parsed.mode === 'extraction';
    const aiActivityMessage = getAiActivityMessage(aiElapsedMs);

    return (
        <Backdrop
            sx={{ color: '#aaa', zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: 'rgba(23, 43, 77, 0.76)' }}
            open={true}
        >
            <Card>
                <TopRow>
                    <div>
                        <Eyebrow>Process Advisor</Eyebrow>
                        <Phase>{parsed.phase}</Phase>
                    </div>
                    {isExtraction && <Percent>{percent}%</Percent>}
                </TopRow>

                {isAi ? (
                    <AiActivity>
                        <AiSpinner aria-label="AI generation in progress" />
                        <AiText>
                            {aiActivityMessage}
                            <AiDots aria-hidden="true"><span /><span /><span /></AiDots>
                        </AiText>
                    </AiActivity>
                ) : isExtraction ? (
                    <>
                        <Detail>{parsed.detail || 'Working through the selected records'}</Detail>

                        <ProgressTrack aria-label="Process Advisor extraction progress">
                            <ProgressFill $width={percent} />
                        </ProgressTrack>
                    </>
                ) : (
                    <GenericActivity>
                        <AiSpinner aria-label="Working" />
                        <AiText>{parsed.detail || 'Working…'}</AiText>
                    </GenericActivity>
                )}

                {isExtraction && (
                    <Metrics>
                        <Metric>
                            <MetricLabel>{parsed.primaryLabel}</MetricLabel>
                            <MetricValue>{parsed.primaryValue}</MetricValue>
                        </Metric>
                        <Metric>
                            <MetricLabel>{parsed.secondaryLabel}</MetricLabel>
                            <MetricValue>{parsed.secondaryValue}</MetricValue>
                        </Metric>
                    </Metrics>
                )}

                <Note>{isAi ? 'Generating AI insights can take up to 120 seconds.' : parsed.note}</Note>
            </Card>
        </Backdrop>
    );
};
